import { useCallback, useRef } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useStore } from "react-redux";
import useAuth from "./useAuth";
import useRefreshToken from "./useRefreshToken";
import useEndSession from "./useEndSession";
import { isAccessTokenValid } from "../services/refreshSession";
import { stopImpersonating } from "../context/impersonation/impersonationSlice";
import { resetActive as resetActiveWinery, empty as emptyWineriesList } from "../context/wineries/wineriesSlice";
import { empty as emptyOperations } from "../context/operations/operationsSlice";
import { emptyExp } from "../context/expOperations/expOperationsSlice";
import { emptyAnalysis } from "../context/protocols/analysisSlice";
import { emptyUsers } from "../context/users/usersSlice";
import { emptyTanks } from "../context/tanks/tanksSlice";
import { emptyLots } from "../context/lots/lotsSlice";

// Il target ha wineries/dati diversi dall'admin: a fine impersonazione si
// azzerano lo stato di impersonazione, la cantina attiva e le cache scoped
// per winery, cosi' l'app riparte pulita.
export const clearImpersonationState = (dispatch) => {
  dispatch(stopImpersonating());
  dispatch(emptyWineriesList());
  dispatch(resetActiveWinery());
  dispatch(emptyOperations());
  dispatch(emptyExp());
  dispatch(emptyAnalysis());
  dispatch(emptyUsers());
  dispatch(emptyTanks());
  dispatch(emptyLots());
};

// Uscita dall'impersonazione quando il BE non l'ha confermata (stop fallito o
// token del target scaduto). Solo locale, nessuna chiamata di stop:
//  - savedAdminAuth ancora valido -> ripristinato com'e';
//  - access token dell'admin scaduto -> il cookie di refresh e' sempre quello
//    dell'admin che ha fatto login (start non lo tocca), quindi un refresh
//    riporta l'admin, ma solo se l'identita' del nuovo token coincide;
//  - refresh fallito o identita' diversa -> sessione chiusa (useEndSession),
//    mai uno stato a meta'. Dopo F5 savedAdminAuth non esiste: si va sempre
//    sul refresh (con il controllo sul claim del token).
// Con il refresh lo stato del target si azzera SOLO dopo aver ottenuto il token
// dell'admin: svuotarlo prima lascerebbe pagine ancora montate che, col token
// del target e senza cantina, ricevono 428 e l'interceptor porta l'admin su
// /#wineriesList (modale di selezione cantina).
// In ogni caso lo svuotamento viene DOPO il cambio di route (flushSync): lo
// store Redux renderizza in modo sincrono e le pagine del target ancora
// montate vedrebbero le liste vuote e le ricaricherebbero come admin, senza
// cantina (500/403 dal BE, toast "accesso negato").
// Idempotente: se l'impersonazione non e' piu' attiva non fa nulla; chiamate
// concorrenti (interceptor e banner) condividono la stessa uscita in corso.
let exitInFlight = null;

const useExitImpersonation = () => {
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const store = useStore();
  const refresh = useRefreshToken();
  const endSession = useEndSession();
  const latest = useRef({});
  latest.current = { setAuth, navigate, dispatch, store, refresh, endSession };

  return useCallback(() => {
    if (exitInFlight) return exitInFlight;
    const { store } = latest.current;
    const { active, savedAdminAuth } = store.getState().impersonation;
    if (!active) return Promise.resolve();

    exitInFlight = (async () => {
      const { setAuth, navigate, dispatch, refresh, endSession } = latest.current;
      if (isAccessTokenValid(savedAdminAuth?.accessToken)) {
        // token dell'admin e cambio di route nello stesso commit, poi lo svuotamento
        flushSync(() => {
          setAuth(savedAdminAuth);
          navigate("/clients");
        });
        clearImpersonationState(dispatch);
        return;
      }

      try {
        // il refresh verifica che l'identita' sia quella dell'admin (id + type)
        await refresh({ savedAdminToken: savedAdminAuth?.accessToken, impersonation: active });
      } catch (err) {
        endSession(); // no-op se il refresh rifiutato l'ha gia' chiusa
        clearImpersonationState(latest.current.dispatch); // niente dati del target nel login successivo
        return;
      }
      // prima la route (la pagina del target si smonta), poi lo svuotamento
      flushSync(() => latest.current.navigate("/clients"));
      clearImpersonationState(latest.current.dispatch);
    })().finally(() => {
      exitInFlight = null;
    });
    return exitInFlight;
  }, []);
};

export default useExitImpersonation;
